import { ChevronDown, ChevronRight, Lock } from 'lucide-react'
import clsx from 'clsx'
import { ChipInput } from '@/components/ChipInput'
import { ConfidenceSlider } from '@/components/ConfidenceSlider'
import { InfoTooltip } from '@/components/InfoTooltip'
import { Select } from '@/components/Select'
import { Tag } from '@/components/Tag'
import { ToggleField } from '@/components/ToggleField'
import { Tooltip } from '@/components/Tooltip'
import { IDENTIFIER_RULE_MESSAGE } from '@/lib/identifierValidation'
import type { LookupTypeOption } from '@/lib/lookupTypes'
import type { DocumentFieldType, DocumentTypeField } from '@/types/bundle'
import { snakeToTitleCase } from '../labels'
import { FIELD_TYPE_LABELS, fromPercent, isSyntheticFieldKey, toPercent } from '../fieldUtils'
import { LookupResolutionSection } from './LookupResolutionSection'
import { PageNumbersInput } from './PageNumbersInput'

export interface FieldRowProps {
  fieldKey: string
  field: DocumentTypeField
  isLocked: boolean
  onChange: (field: DocumentTypeField) => void
  onDelete: () => void
  onDuplicate: () => void
  expanded: boolean
  onToggleExpanded: () => void
  resolvedDefault: number
  serverErrors?: Record<string, string>
  /** True for a row born this session via "+ Add Field"/"Duplicate" - plays a
   * one-off entrance transition instead of appearing abruptly. Existing
   * fields (already mounted before the operator acts) never get this. */
  isNew?: boolean
  lookupTypeOptions: LookupTypeOption[]
  /** This row's response_tag isn't snake_case (PRD 34) - Save is already
   * blocked while true, this only drives the inline message. */
  isInvalidName?: boolean
}

const FIELD_TYPES: DocumentFieldType[] = [
  'text',
  'money',
  'date_iso',
  'boolean',
  'person_name',
  'structured_address',
]

export function FieldRow({
  fieldKey,
  field,
  isLocked,
  onChange,
  onDelete,
  onDuplicate,
  expanded,
  onToggleExpanded,
  resolvedDefault,
  serverErrors,
  isNew,
  lookupTypeOptions,
  isInvalidName,
}: FieldRowProps) {
  const displayName = isSyntheticFieldKey(fieldKey) ? 'New field' : snakeToTitleCase(fieldKey)
  const currentConfidence = field.acceptance?.min_confidence ?? resolvedDefault
  // PRD 35 (real incident): a field only genuinely needs protecting from
  // rename once it actually has a response_tag - a non-synthetic key with a
  // blank tag (should no longer happen via Duplicate after this PRD, kept as
  // a backstop for any other path or an already-corrupted draft) is never
  // truly "locked": there's nothing valid there yet to protect, and locking
  // it anyway would hide the one way to fix it (disabled input, suppressed
  // "required" message - exactly how Kashif's real incident went unnoticed).
  const effectivelyLocked = isLocked && field.response_tag.trim().length > 0

  return (
    <div
      className={clsx(
        'flex flex-col gap-4 rounded-control border border-border bg-surface p-4',
        isNew && 'animate-[field-row-enter_200ms_ease-out]',
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onToggleExpanded}
          className="flex flex-1 items-center gap-2 text-left text-body font-medium text-text-primary"
        >
          {expanded ? (
            <ChevronDown size={16} strokeWidth={1.5} className="text-text-secondary" />
          ) : (
            <ChevronRight size={16} strokeWidth={1.5} className="text-text-secondary" />
          )}
          {displayName}
          <Tag label={FIELD_TYPE_LABELS[field.field_type]} />
          {field.critical && <Tag label="Critical" variant="danger" />}
          <span className="text-meta text-text-secondary">{toPercent(currentConfidence)}%</span>
        </button>
        <div className="flex items-center gap-3">
          <button type="button" onClick={onDuplicate} className="text-meta font-medium text-accent hover:underline">
            Duplicate
          </button>
          <button type="button" onClick={onDelete} className="text-meta font-medium text-danger hover:underline">
            Delete
          </button>
        </div>
      </div>

      {expanded && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <span className="flex items-center gap-2 text-meta font-medium text-text-secondary">
              Response tag
              {effectivelyLocked && (
                <Tooltip text="Cannot change after creation - this is the API's contract with Proclaim">
                  <span tabIndex={0} className="text-text-disabled">
                    <Lock size={12} strokeWidth={1.5} />
                  </span>
                </Tooltip>
              )}
            </span>
            <input
              type="text"
              value={field.response_tag}
              disabled={effectivelyLocked}
              onChange={(event) => onChange({ ...field, response_tag: event.target.value })}
              className="w-64 rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none disabled:cursor-not-allowed disabled:text-text-disabled"
            />
            {serverErrors?.response_tag && (
              <span className="text-meta text-danger">{serverErrors.response_tag}</span>
            )}
            {!effectivelyLocked && field.response_tag.trim().length === 0 && (
              <span className="text-meta text-danger">Response tag is required</span>
            )}
            {!effectivelyLocked && field.response_tag.trim().length > 0 && isInvalidName && (
              <span className="text-meta text-danger">{IDENTIFIER_RULE_MESSAGE}</span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-meta font-medium text-text-secondary" htmlFor={`field-type-${fieldKey}`}>
              Field type
            </label>
            <Select
              id={`field-type-${fieldKey}`}
              value={field.field_type}
              onChange={(value) => onChange({ ...field, field_type: value as DocumentFieldType })}
              options={FIELD_TYPES.map((type) => ({ value: type, label: FIELD_TYPE_LABELS[type] }))}
              className="w-48"
            />
          </div>

          <ToggleField
            checked={field.critical ?? false}
            onChange={(critical) => onChange({ ...field, critical })}
            label="Critical"
            tooltip="Critical fields need a higher confidence score before they're accepted. Use this for values that must be correct, like prices or dates."
          />

          <div className="flex flex-col gap-1">
            <span className="flex items-center gap-1 text-meta font-medium text-text-secondary">
              Min confidence
              <InfoTooltip text="How sure the system must be before it accepts this field's value. The tick mark shows the platform's normal default." />
            </span>
            <ConfidenceSlider
              value={toPercent(currentConfidence)}
              referenceValue={toPercent(resolvedDefault)}
              onChange={(percent) =>
                onChange({ ...field, acceptance: { ...field.acceptance, min_confidence: fromPercent(percent) } })
              }
            />
          </div>

          <div className="flex flex-col gap-1">
            <span className="flex items-center gap-1 text-meta font-medium text-text-secondary">
              Prompt hints
              <InfoTooltip text="Example phrases that help find this field on the page, like 'sale price' or 'agreed purchase price'." />
            </span>
            <ChipInput
              value={field.prompt_hints ?? []}
              onChange={(prompt_hints) => onChange({ ...field, prompt_hints })}
              placeholder="Type a phrase, press Enter…"
            />
          </div>

          <div className="flex flex-col gap-1">
            <span className="flex items-center gap-1 text-meta font-medium text-text-secondary">
              Page hint
              <InfoTooltip text="Page(s) this field is usually on. Only used when every field of this document type has a page hint: the model is then first shown just those pages, and nearby pages are checked if a field isn't found. Leave empty if the page isn't predictable." />
            </span>
            <PageNumbersInput
              value={field.page_hint ?? []}
              onChange={(pages) => onChange({ ...field, page_hint: pages.length > 0 ? pages : undefined })}
            />
            {serverErrors?.page_hint && <span className="text-meta text-danger">{serverErrors.page_hint}</span>}
          </div>

          {field.lookup && (
            <LookupResolutionSection
              lookup={field.lookup}
              onChange={(lookup) => onChange({ ...field, lookup })}
              typeOptions={lookupTypeOptions}
            />
          )}
        </div>
      )}
    </div>
  )
}
